import React, { useEffect, useRef } from 'react';
import { Form } from 'react-bootstrap';
import ImageUploader from './ImageUploader';
import ComponentsCollectionEditor from './ComponentsCollectionEditor';

/**
 * Common fields for Page editors: Slug, Title, and Preview image.
 * Props:
 *  - form: object with keys { slug, title, preview }
 *  - setForm: setState function to update form
 *  - websiteId: id required by ImageUploader
 *  - slugPlaceholder?: optional placeholder for slug input
 *  - titlePlaceholder?: optional placeholder for title input
 */
function slugify(value) {
  const s = String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, '-') // spaces/underscores -> hyphen
    .replace(/[^a-z0-9-]/g, '') // remove non-allowed
    .replace(/-+/g, '-') // collapse hyphens
    .replace(/^-+|-+$/g, ''); // trim hyphens
  return s;
}

function isValidSlug(s) {
  const v = String(s || '').trim();
  if (!v) return false;
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(v);
}

function PageBasicFields({ form, setForm, websiteId, slugPlaceholder = 'e.g. products', titlePlaceholder = 'Enter title', preserveEmptySlug = false }) {
  const slugTouchedManually = useRef(false);
  const lastAutoSlugRef = useRef('');

  // When title changes from outside (prefilled or programmatic) and slug is empty & untouched, set an auto slug
  useEffect(() => {
    if (!preserveEmptySlug && (form.slug || '').trim() === '' && !slugTouchedManually.current) {
      const auto = slugify(form.title);
      lastAutoSlugRef.current = auto;
      if (auto) {
        setForm(prev => ({ ...prev, slug: auto }));
      }
    }
    // We intentionally depend on form.title and form.slug to react to preloaded data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.title, form.slug, preserveEmptySlug]);

  return (
    <>
      <div className="form-row-horizontal mb-2">
        <Form.Label>Title</Form.Label>
        <Form.Control
          type="text"
          value={form.title}
          onChange={(e) => {
            const nextTitle = e.target.value;
            setForm(prev => {
              const next = { ...prev, title: nextTitle };
              // Auto-build slug if user hasn't manually modified it yet,
              // or if current slug equals last auto-generated one.
              if (!preserveEmptySlug && (!slugTouchedManually.current || (prev.slug || '') === (lastAutoSlugRef.current || ''))) {
                const auto = slugify(nextTitle);
                lastAutoSlugRef.current = auto;
                next.slug = auto;
              }
              return next;
            });
          }}
          placeholder={titlePlaceholder}
        />
      </div>

        <div className="form-row-horizontal mb-2">
            <Form.Label>Slug</Form.Label>
            <Form.Control
                type="text"
                value={form.slug}
                isInvalid={(form.slug || '').trim() !== '' && !isValidSlug(form.slug)}
                onChange={(e) => {
                  slugTouchedManually.current = true;
                  const raw = e.target.value;
                  setForm({ ...form, slug: raw });
                }}
                onBlur={(e) => {
                  const sanitized = slugify(e.target.value);
                  lastAutoSlugRef.current = sanitized; // keep in sync even for manual edit after sanitize
                  setForm(prev => ({ ...prev, slug: sanitized }));
                }}
                placeholder={slugPlaceholder}
            />
        </div>

      <div className="form-row-horizontal mb-2">
        <Form.Label>Preview</Form.Label>
        <ImageUploader websiteId={websiteId} onUploaded={(resp) => { setForm({ ...form, preview: resp.url }); }} />
        {form.preview && (
          <img src={form.preview} alt="Preview" style={{ maxHeight: '4rem', marginTop: 8 }} />
        )}
      </div>

      <div className="form-row-horizontal mb-2">
        <ComponentsCollectionEditor
          websiteId={websiteId}
          value={form.components || { items: [] }}
          onChange={(val) => {
            setForm(prev => ({
              ...prev,
              components: val,
            }));
          }}
        />
      </div>
    </>
  );
}

export default PageBasicFields;
